import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Typography,
  createTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { useFormContext } from "react-hook-form";
import { ChangeOffer, CustomerSentOffersState } from "../../models/models";
import { useParams } from "react-router-dom";
import { getCategories, getManufacturers, getRfq, setLoading } from "../../customerSentOffersSlice";
import { FileModel, WarrantyTypeCode, enumToArray, warranty } from "../../../../shared/models/models";
import { ProductAvailabilityCode, productAvailability } from "../../../inventory/models/models";
import UploadFile from "../../../../shared/components/UploadFile";
import useTranslation from "../../../../shared/hooks/useTranslation";

const ShippingAndOtherDetailsStep = () => {
  const { rfq, manufacturers, categories } = useAppSelector<CustomerSentOffersState>(
    (state: any) => state.customerSentOffers
  );
  const dispatch = useAppDispatch();

  const {
    register,
    trigger,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext<ChangeOffer>();

  const { id } = useParams();
  const [file, setFile] = useState<FileModel | null>(getValues("imageFile"));
  const { translate } = useTranslation("inventory");
  const { translate: tCommon } = useTranslation("common");

  const handlerCreateFile = (file: FileModel | null) => {
    setValue("imageFile", file);
    setFile(file);
  };

  const init = async () => {
    dispatch(setLoading(true));
    if (id) {
      await dispatch(getRfq(id));
    }
    register("imageFile", {});
    await dispatch(getManufacturers());
    await dispatch(getCategories());
    dispatch(setLoading(false));
  };

  useEffect(() => {
    init();
  }, []);
  const theme = createTheme({});

  return (
    <ThemeProvider theme={theme}>
      <Box className="mt-5">
        <Box className="heading-1">Create new offer</Box>

        <Typography fontSize={20} fontWeight={500} className="text-20-medium flex">
          for{"\u00A0"}
          <span>
            <Typography fontSize={20} fontWeight={700}>
              {rfq?.buyer.name}
            </Typography>
          </span>
        </Typography>
      </Box>
      <Box className="mt-5">
        <Box className="heading-2">Other product details</Box>
      </Box>

      <Box className="mt-3">
        <InputLabel required>Brand</InputLabel>
        <FormControl className="w-full">
          <Select
            key="manufacturerId"
            defaultValue={rfq?.manufacturer.id || ""}
            onClose={() => setTimeout(() => trigger("manufacturerId"))}
            {...register("manufacturerId", { required: tCommon("forms.required") })}
            error={!!errors.manufacturerId}
          >
            {manufacturers.map((manufacturer) => (
              <MenuItem key={manufacturer.id} value={manufacturer.id}>
                {manufacturer.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error>{errors.manufacturerId?.message}</FormHelperText>
        </FormControl>
      </Box>

      <Box className="mt-4 grid grid-cols-2 gap-4">
        <Box>
          <InputLabel required>Product number</InputLabel>
          <TextField
            className="w-full"
            defaultValue={rfq?.productNumber || ""}
            {...register("productNumber", { required: tCommon("forms.required") })}
            error={!!errors.productNumber}
            helperText={errors.productNumber?.message}
          />
        </Box>

        <Box>
          <InputLabel>Alternative number</InputLabel>
          <TextField
            className="w-full"
            defaultValue={rfq?.productAlternativeNumber || ""}
            {...register("productAlternativeNumber")}
            error={!!errors.productAlternativeNumber}
            helperText={errors.productAlternativeNumber?.message}
          />
        </Box>
      </Box>

      <Box className="mt-4">
        <InputLabel required>Category</InputLabel>
        <FormControl className="w-full">
          <Select
            key="categoryId"
            defaultValue={rfq?.category?.id || ""}
            onClose={() => setTimeout(() => trigger("categoryId"))}
            {...register("categoryId", { required: tCommon("forms.required") })}
            error={!!errors.categoryId}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error>{errors.categoryId?.message}</FormHelperText>
        </FormControl>
      </Box>

      <Box className="mt-4 grid grid-cols-2 gap-4">
        <Box>
          <InputLabel required>Lead time</InputLabel>
          <FormControl className="w-full">
            <Select
              defaultValue=""
              className="w-full"
              onClose={() => setTimeout(() => trigger("availabilityCode"))}
              {...register("availabilityCode", { required: tCommon("forms.required") })}
              error={!!errors.availabilityCode}
            >
              {enumToArray<ProductAvailabilityCode>(ProductAvailabilityCode).map((code) => (
                <MenuItem key={code} value={code}>
                  {productAvailability(code, translate as (key: string, options?: any) => string)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error>{errors.availabilityCode?.message}</FormHelperText>
          </FormControl>
        </Box>

        <Box>
          <InputLabel>warranty</InputLabel>
          <Select defaultValue="" className="w-full" {...register("warrantyPeriod")}>
            {enumToArray<WarrantyTypeCode>(WarrantyTypeCode).map((code) => (
              <MenuItem key={code} value={code || ""}>
                {warranty(code)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box className="mt-4">
        <InputLabel>Product image</InputLabel>
        <Box className="grid grid-cols-1 mt-1 gap-4 w-full">
          <UploadFile file={file} variant="image" onChangeFile={handlerCreateFile} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ShippingAndOtherDetailsStep;